import {Requirement, RegulatoryDocumentParagraph} from 'types';
import {TFunction} from 'react-i18next';
import Excel, {RichText} from 'exceljs';
import {funcDecodeHex24ToDocId} from 'components/InfinityList';
import {
	createZIPXL,
	IArrayRoles,
	renderArrayAsRichtText,
	renderHeaderXL,
	renderHTMLStringXL,
	renderRichtText,
} from 'components/InfinityList/Excel/ExcelExportCommon';
import {QuestionCountsByEntityTypeQuery} from 'components/Faq/Faq.generated';
import {renderParagraphListXL} from './XLParagraphList';

interface IRequirementDetailPage {
	(
		entityListItems: any[],
		arrRoles: IArrayRoles,
		pageDetails: any,
		t: TFunction,
	): Promise<void>;
}

export const handleClickRequirementDetailPage: IRequirementDetailPage = async (
	entityListItems,
	arrRoles,
	pageDetails,
	t,
) => {
	const requirement = pageDetails as Requirement;
	const questionSummaryData = pageDetails.questionSummaryData as
		| QuestionCountsByEntityTypeQuery
		| undefined;
	const arrParaFilt = entityListItems as RegulatoryDocumentParagraph[];

	const arrImagesUri: string[] = [];
	const arrImagesName: string[] = [];
	const arrTables: string[] = [];


	const tempDivElement = document.createElement('div');

	const workbook = new Excel.Workbook();
	const arrColWidth: number[] = [
		...new Array(2).fill(25),
		100,
		...new Array(2).fill(25),
		100,
		...new Array(11).fill(25),
	];

	const tOptions = {ns: 'features/requirements'};

	const getHeadingT = (rawHeading: string) => {
		return t(rawHeading, tOptions);
	};

	const strPageName = t('AssignedParagraphs', tOptions);
	const sheet = workbook.addWorksheet('TRACE_' + strPageName);

	renderHeaderXL(workbook, sheet, arrColWidth, strPageName, t);

	let intRowCount = 4;
	let row = sheet.getRow(intRowCount);
	row.alignment = {wrapText: true, vertical: 'top'};

	row.getCell('B').style.font = {
		underline: true,
		bold: false,
		color: {argb: '00B0F0'},
	};

	row.getCell('B').value = {
		text: requirement.requirementId ?? '',
		hyperlink: window.location.origin + '/requirements/' + requirement.id,
	};
	row.getCell('C').value = renderRichtText(
		getHeadingT('Version'),
		requirement.version ?? '-',
	);
	row.getCell('D').value = renderRichtText(
		getHeadingT('Name'),
		requirement.name ?? '',
	);
	row.getCell('E').value = renderRichtText(
		getHeadingT('Category'),
		t(requirement.category, {
			ns: 'common/enums',
			keyPrefix: 'RequirementCategory',
		}),
	);

	intRowCount++;

	row = sheet.getRow(intRowCount);
	row.alignment = {wrapText: true, vertical: 'top'};

	row.getCell('B').value = renderRichtText(
		getHeadingT('CreatedBy'),
		requirement.createdBy?.name ?? '',
	);
	row.getCell('C').value = renderArrayAsRichtText(
		getHeadingT('VexCluster'),
		requirement.vexClusters,
	);

	row.getCell('D').value = renderHTMLStringXL(
		t('Definition', {ns: 'features/requirements'}),
		requirement.definition,
	);

	row.getCell('E').value = renderRichtText(
		getHeadingT('Status'),
		t(requirement.status ?? '', {
			ns: 'common/enums',
			keyPrefix: 'RequirementStatus',
		}),
	);

	intRowCount++;

	for (
		let jj = 0;
		jj < tempDivElement.getElementsByTagName('img').length;
		jj++
	) {
		arrImagesUri.push(
			tempDivElement
				.getElementsByTagName('img')[0]
				.getAttribute('src') as string,
		);
		arrImagesName.push(
			(requirement.requirementId as string) + '_' + jj + '.html',
		);
		row = sheet.getRow(intRowCount);
		row.outlineLevel = 1;
		row.getCell('B').fill = {
			type: 'pattern',
			pattern: 'solid',
			fgColor: {argb: 'BFBFBF'},
		};

		row.getCell('D').alignment = {
			indent: 1,
			wrapText: true,
			vertical: 'top',
		};
		row.getCell('D').style.font = {
			underline: true,
			bold: false,
			color: {argb: '00B0F0'},
		};
		row.getCell('D').value = {
			text: t('LinkImage', {ns: 'components/EntityList/ExcelExport'}),
			hyperlink:
				(('assets/' + requirement.requirementId) as string) +
				'_' +
				jj +
				'.html',
			tooltip: t('Link', {
				ns: 'components/EntityList/ExcelExport',
				keyPrefix: 'TooltipsText',
			}),
		};

		intRowCount++;
	}

	row = sheet.getRow(intRowCount);
	row.alignment = {wrapText: true, vertical: 'top'};

	row.getCell('B').value = renderArrayAsRichtText(
		getHeadingT('SystemLevels'),
		requirement.systemLevels,
	);
	row.getCell('C').value = renderArrayAsRichtText(
		getHeadingT('Markets'),
		requirement.markets,
	);
	row.getCell('D').value = renderArrayAsRichtText(
		getHeadingT('Keywords'),
		requirement.keywords,
	);
	row.getCell('E').value = renderArrayAsRichtText(
		getHeadingT('Tags'),
		requirement.tags,
	);

	intRowCount++;
	row = sheet.getRow(intRowCount);
	row.alignment = {wrapText: true, vertical: 'top'};

	row.getCell('B').value = renderArrayAsRichtText(
		getHeadingT('DriveVariants'),
		requirement.driveVariants,
	);
	row.getCell('C').value = renderArrayAsRichtText(
		getHeadingT('EngineVariants'),
		requirement.engineVariants,
	);
	row.getCell('D').value = renderArrayAsRichtText(
		getHeadingT('GearboxVariants'),
		requirement.gearboxVariants,
	);
	row.getCell('E').value = renderArrayAsRichtText(
		getHeadingT('BodyworkVariants'),
		requirement.bodyworkVariants,
	);

	intRowCount++;
	row = sheet.getRow(intRowCount);
	row.alignment = {wrapText: true, vertical: 'top'};

	row.getCell('B').value = renderArrayAsRichtText(
		getHeadingT('ActiveTypeMarkets'),
		requirement.activeTypeMarkets,
	);
	row.getCell('C').value = renderArrayAsRichtText(
		getHeadingT('VehicleCategory'),
		requirement.vehicleCategories,
	);
	row.getCell('D').value = renderRichtText(
		'Q&A',
		`${questionSummaryData?.questionCountsByEntityType.at(0)?.count ?? 0}`,
	);

	intRowCount++;
	row = sheet.getRow(intRowCount);
	row.alignment = {wrapText: true, vertical: 'top'};
	row.getCell('B').value = renderRichtText(
		getHeadingT('Attachments'),
		(requirement.attachments ?? []).length == 0 ? '-' : '',
	);

	(requirement.attachments ?? []).forEach(iAttach => {
		intRowCount++;
		row = sheet.getRow(intRowCount);
		row.getCell('B').value = {
			text: iAttach.attachmentId,
			hyperlink: iAttach.file.uri,
			tooltip: t('Link', {
				ns: 'components/EntityList/ExcelExport',
				keyPrefix: 'TooltipsText',
			}),
		};
		row.getCell('B').style.font = {
			underline: true,
			bold: false,
			color: {argb: '00B0F0'},
		};
	});

	intRowCount++;
	intRowCount++;

	renderParagraphListXL(
		intRowCount,
		false,
		arrTables,
		arrImagesName,
		arrImagesUri,
		arrParaFilt,
		arrRoles,
		sheet,
		t,
	);

	const strZipName = strPageName + '_' + funcDecodeHex24ToDocId(requirement.id);
	await createZIPXL(
		workbook,
		strZipName,
		t,
		arrTables,
		arrImagesUri,
		arrImagesName,
		requirement.attachments!,
	);
};
